/**
 * Tenant statements: period/status filters, lines, retry and waive (TMMPDM-0008).
 * TMMPDM-0035: the status filter lives in the URL (`status`, beside `?tab=`); sticky headers;
 * filtered-empty Clear. The period box stays a typed field (the spec's URL keys name status only).
 */
import { Fragment, useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { EmptyState, ErrorState, useNotify } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { Amount, stickyTableContainerSx } from '@/components/ui';
import { CHARGE_STATUSES, type ChargeStatus, type StatementView } from '@/api/tmm/types';
import { WaiveStatementDialog } from './WaiveStatementDialog';
import { errorCopy } from '@/api/errors';
import { formatDate, formatUnitMicro } from '@/format';
import { StatusChip } from '@/status';
import { statementSupportDetails } from '@/routes/ai/statementSupportDetails';

interface StatementsPanelProps {
  slug: string;
  currency: string;
}

const PERIOD_RE = /^\d{4}-\d{2}$/;

type StatementsView = { status: ChargeStatus | '' };
const VIEW: UrlSchema<StatementsView> = {
  status: { default: '', parse: oneOf(CHARGE_STATUSES) },
};

function canRetry(status: ChargeStatus): boolean {
  return status === 'FAILED' || status === 'ACTION_REQUIRED' || status === 'ATTENTION';
}

function canWaive(status: ChargeStatus): boolean {
  return status !== 'PAID' && status !== 'WAIVED';
}

export function StatementsPanel({ slug }: StatementsPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<StatementView[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [periodInput, setPeriodInput] = useState('');
  const [period, setPeriod] = useState('');
  const [view, setView] = useUrlState(VIEW);
  const status = view.status;
  const [expanded, setExpanded] = useState<number | null>(null);
  const [waiveId, setWaiveId] = useState<number | null>(null);

  const periodError =
    periodInput !== '' && !PERIOD_RE.test(periodInput)
      ? t('statements.periodInvalid', 'Use YYYY-MM.')
      : undefined;

  useEffect(() => {
    let cancelled = false;
    const query = {
      ...(period ? { period } : {}),
      ...(status ? { status } : {}),
    };
    adminApi
      .listStatements(slug, Object.keys(query).length > 0 ? query : undefined)
      .then((list) => {
        if (cancelled) return;
        setRows(list.statements);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, period, status, reloadToken]);

  const clearFilters = () => {
    setPeriodInput('');
    setPeriod('');
    setView({ status: '' });
  };

  const retry = async (id: number) => {
    setActionError(null);
    try {
      await adminApi.retryStatement(slug, id);
      setReloadToken((n) => n + 1);
    } catch (err: unknown) {
      setActionError(
        errorCopy(err, t, t('statements.retryError', "Couldn't retry the statement.")),
      );
    }
  };

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('statements.loadError', 'Could not load statements.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  return (
    <Stack spacing={2}>
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
        <TextField
          label={t('statements.periodFilter', 'Period')}
          value={periodInput}
          onChange={(event) => {
            const value = event.target.value;
            setPeriodInput(value);
            if (value === '' || PERIOD_RE.test(value)) setPeriod(value);
          }}
          error={Boolean(periodError)}
          helperText={periodError}
          placeholder="YYYY-MM"
          slotProps={{ htmlInput: { pattern: '\\d{4}-\\d{2}' } }}
        />
        <TextField
          select
          label={t('statements.statusFilter', 'Status')}
          value={status}
          onChange={(event) => setView({ status: event.target.value as ChargeStatus | '' })}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('statements.statusAll', 'All')}</MenuItem>
          {CHARGE_STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`statements.chargeStatus.${value}`, value)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
        <Table
          size="small"
          stickyHeader
          aria-label={t('statements.tableAria', 'Tenant statements')}
        >
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('statements.col.period', 'Period')}</TableCell>
              <TableCell>{t('statements.col.status', 'Status')}</TableCell>
              <TableCell>{t('statements.col.renewal', 'Renewal')}</TableCell>
              <TableCell>{t('statements.col.seats', 'Seats')}</TableCell>
              <TableCell>{t('statements.col.tax', 'Tax')}</TableCell>
              <TableCell>{t('statements.col.total', 'Total')}</TableCell>
              <TableCell>{t('statements.col.attempts', 'Attempts')}</TableCell>
              <TableCell>{t('statements.col.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows === null ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t('common.loading', 'Loading')}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <EmptyState
                    title={t('statements.empty', 'No statements.')}
                    {...(period !== '' || status !== ''
                      ? { filtered: { onClear: clearFilters } }
                      : {})}
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const open = expanded === row.id;
                return (
                  <Fragment key={row.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={
                            open
                              ? t('statements.collapse', 'Collapse lines')
                              : t('statements.expand', 'Expand lines')
                          }
                          onClick={() => setExpanded(open ? null : row.id)}
                        >
                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {formatDate(row.periodStart, locale)} – {formatDate(row.periodEnd, locale)}
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={row.chargeStatus}
                          label={t(`statements.chargeStatus.${row.chargeStatus}`, row.chargeStatus)}
                        />
                      </TableCell>
                      <TableCell>
                        <Amount minor={row.renewalMinor} currency={row.currency} locale={locale} />
                      </TableCell>
                      <TableCell>
                        {row.seats} ×{' '}
                        <Amount minor={row.seatsMinor} currency={row.currency} locale={locale} />
                      </TableCell>
                      <TableCell>
                        <Amount minor={row.taxMinor} currency={row.currency} locale={locale} />
                      </TableCell>
                      <TableCell>
                        <strong>
                          <Amount minor={row.totalMinor} currency={row.currency} locale={locale} />
                        </strong>
                      </TableCell>
                      <TableCell
                        title={
                          row.lastAttemptAt ? formatDate(row.lastAttemptAt, locale) : undefined
                        }
                      >
                        {row.attempts}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {canRetry(row.chargeStatus) ? (
                            <Button size="small" onClick={() => void retry(row.id)}>
                              {t('statements.retry', 'Retry')}
                            </Button>
                          ) : null}
                          {canWaive(row.chargeStatus) ? (
                            <Button size="small" onClick={() => setWaiveId(row.id)}>
                              {t('statements.waive', 'Waive…')}
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{ py: 0, borderBottom: open ? undefined : 'none' }}
                      >
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <BoxLines row={row} locale={locale} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <WaiveStatementDialog
        open={waiveId != null}
        slug={slug}
        statementId={waiveId ?? 0}
        onClose={() => setWaiveId(null)}
        onDone={() => {
          setWaiveId(null);
          setReloadToken((n) => n + 1);
        }}
      />
    </Stack>
  );
}

function BoxLines({ row, locale }: { row: StatementView; locale: string }) {
  const { t } = useCopy();
  const notify = useNotify();
  // The raw `lastError` is only ever copied for support, never shown (TMM-B3, TMMPDM-0075 A1).
  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(statementSupportDetails(row));
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };
  return (
    <Stack spacing={1} sx={{ py: 1 }}>
      <Table size="small" aria-label={t('statements.lines.kind', 'Kind')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('statements.lines.kind', 'Kind')}</TableCell>
            <TableCell>{t('statements.lines.description', 'Description')}</TableCell>
            <TableCell>{t('statements.lines.quantity', 'Quantity')}</TableCell>
            <TableCell>{t('statements.lines.unit', 'Unit')}</TableCell>
            <TableCell>{t('statements.lines.amount', 'Amount')}</TableCell>
            <TableCell>{t('statements.lines.reference', 'Reference')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {row.lines.map((line, index) => (
            <TableRow key={`${line.kind}-${line.reference ?? index}`}>
              <TableCell>{t(`statements.kind.${line.kind}`, line.kind)}</TableCell>
              <TableCell>{line.description ?? '—'}</TableCell>
              <TableCell>{line.quantity ?? '—'}</TableCell>
              <TableCell>
                {line.unitMicro == null
                  ? '—'
                  : formatUnitMicro(line.unitMicro, row.currency, locale)}
              </TableCell>
              <TableCell>
                <Amount minor={line.amountMinor} currency={row.currency} locale={locale} />
              </TableCell>
              <TableCell>{line.reference ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {row.chargeStatus === 'FAILED' || row.lastError ? (
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
          {row.chargeStatus === 'FAILED' ? (
            <Typography variant="caption" color="error">
              {t('components.lastAttemptFailed', 'The last attempt failed.')}
            </Typography>
          ) : null}
          {row.lastError ? (
            <Button
              size="small"
              variant="outlined"
              aria-label={t(
                'statements.copyDetailsAria',
                'Copy details for support for statement {{id, number}}',
                { id: row.id },
              )}
              onClick={() => void copyDetails()}
            >
              {t('components.copyDetails', 'Copy details for support')}
            </Button>
          ) : null}
        </Stack>
      ) : null}
      {row.payApiChargeRef ? (
        <Typography variant="caption" color="text.secondary">
          {row.payApiChargeRef}
        </Typography>
      ) : null}
    </Stack>
  );
}
